import React, { useCallback, useEffect, useState } from 'react';
import {
  getAlertMeta, listAlertRules, createAlertRule, updateAlertRule, setAlertRuleEnabled, deleteAlertRule,
  listAlerts, acknowledgeAlert, resolveAlert, acknowledgeAllAlerts,
} from '../api/alertsApi';
import { errorMessage } from '../api/securityApi';
import SecurityNav from '../components/Security/SecurityNav';
import SiteSelector, { useActiveSite } from '../components/Security/SiteSelector';
import { SevBadge } from '../components/Security/Charts';
import RequirePermission from '../auth/RequirePermission';

const OP_LABEL = { Gt: '>', Gte: '≥', Lt: '<' };
const blank = {
  name: '', siteKey: '', metric: 'ThreatsCount', operator: 'Gte', threshold: 10,
  windowMinutes: 60, severity: 'Medium', cooldownMinutes: 60, isEnabled: true,
};
const utc = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function AlertsPage() {
  const { sites } = useActiveSite();
  const [tab, setTab] = useState('inbox');
  const [meta, setMeta] = useState({ metrics: [], operators: [], severities: [] });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // inbox
  const [status, setStatus] = useState('Open');
  const [page, setPage] = useState(1);
  const [inbox, setInbox] = useState({ items: [], totalCount: 0, pageSize: 25 });

  // rules
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(null); // null = closed; object = editing/creating
  const [editingId, setEditingId] = useState(null);

  const metricLabel = (v) => meta.metrics.find((m) => m.value === v)?.label || v;

  const loadInbox = useCallback(async () => {
    try { setInbox(await listAlerts({ status, page, pageSize: 25 })); }
    catch (e) { setError(errorMessage(e)); }
  }, [status, page]);
  const loadRules = useCallback(async () => {
    try { setRules(await listAlertRules()); } catch (e) { setError(errorMessage(e)); }
  }, []);

  useEffect(() => { getAlertMeta().then(setMeta).catch((e) => setError(errorMessage(e))); }, []);
  useEffect(() => { loadInbox(); }, [loadInbox]);
  useEffect(() => { loadRules(); }, [loadRules]);

  const act = async (fn) => {
    setError(''); setInfo('');
    try { await fn(); await Promise.all([loadInbox(), loadRules()]); }
    catch (e) { setError(errorMessage(e)); }
  };

  const save = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      siteKey: form.siteKey || null,
      threshold: Number(form.threshold),
      windowMinutes: Number(form.windowMinutes),
      cooldownMinutes: Number(form.cooldownMinutes),
    };
    await act(async () => {
      if (editingId) await updateAlertRule(editingId, body); else await createAlertRule(body);
      setForm(null); setEditingId(null); setInfo('Rule saved.');
    });
  };

  const edit = (r) => {
    setEditingId(r.id);
    setForm({ ...r, siteKey: r.siteKey || '' });
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const pages = Math.max(1, Math.ceil(inbox.totalCount / (inbox.pageSize || 25)));

  return (
    <div className="shell">
      <header className="page-header"><div><h1>Alerts</h1>
        <p className="muted">Get told when traffic quality or volume crosses a limit you choose.</p></div></header>
      <SecurityNav />
      <div className="sec-toolbar">
        <button className={tab === 'inbox' ? 'primary' : 'ghost'} onClick={() => setTab('inbox')}>Inbox</button>
        <button className={tab === 'rules' ? 'primary' : 'ghost'} onClick={() => setTab('rules')}>Rules ({rules.length})</button>
      </div>
      {error && <div className="toast error">{error}</div>}
      {info && <div className="toast info">{info}</div>}

      {tab === 'inbox' && (
        <div className="sec-card sec-scroll">
          <div className="sec-toolbar">
            <select className="sec-select" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }}>
              <option value="">All statuses</option>
              {['Open', 'Acknowledged', 'Resolved'].map((s) => <option key={s}>{s}</option>)}
            </select>
            <RequirePermission code="alerts.manage">
              <button className="ghost" onClick={() => act(acknowledgeAllAlerts)}>Acknowledge all</button>
            </RequirePermission>
          </div>
          <table className="sec-table">
            <thead><tr><th>When</th><th>Alert</th><th>Severity</th><th>Details</th><th>Status</th><th /></tr></thead>
            <tbody>
              {inbox.items.map((a) => (
                <tr key={a.id}>
                  <td>{utc(a.createdAt)}</td>
                  <td><b>{a.title}</b></td>
                  <td><SevBadge value={a.severity} /></td>
                  <td className="muted">{a.message}</td>
                  <td>{a.status}</td>
                  <td>
                    <RequirePermission code="alerts.manage">
                      <div className="sec-row">
                        {a.status === 'Open' && <button className="ghost" onClick={() => act(() => acknowledgeAlert(a.id))}>Acknowledge</button>}
                        {a.status !== 'Resolved' && <button className="ghost" onClick={() => act(() => resolveAlert(a.id))}>Resolve</button>}
                      </div>
                    </RequirePermission>
                  </td>
                </tr>
              ))}
              {inbox.items.length === 0 && <tr><td colSpan="6" className="muted">No alerts here. Create a rule to start monitoring.</td></tr>}
            </tbody>
          </table>
          <div className="sec-pager">
            <span className="muted">{inbox.totalCount} total</span>
            <button className="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
            <span>{page} / {pages}</span>
            <button className="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        </div>
      )}

      {tab === 'rules' && (
        <>
          <RequirePermission code="alerts.manage">
            {!form && <button className="primary" style={{ marginBottom: 12 }} onClick={() => { setEditingId(null); setForm({ ...blank }); }}>+ New alert rule</button>}
          </RequirePermission>

          {form && (
            <form className="sec-card" onSubmit={save} style={{ marginBottom: 16 }}>
              <h3>{editingId ? 'Edit rule' : 'New alert rule'}</h3>
              <div className="sec-field"><label>Name</label>
                <input className="sec-input" required maxLength={120} value={form.name} onChange={set('name')} placeholder="e.g. Too many blocked bots" /></div>
              <div className="sec-field"><label>Site</label>
                <SiteSelector sites={sites} value={form.siteKey} onChange={(v) => setForm({ ...form, siteKey: v })} /></div>
              <div className="sec-field"><label>Condition</label>
                <div className="sec-row">
                  <select className="sec-select" value={form.metric} onChange={set('metric')}>
                    {meta.metrics.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select>
                  <select className="sec-select" style={{ minWidth: 70 }} value={form.operator} onChange={set('operator')}>
                    {meta.operators.map((o) => <option key={o} value={o}>{OP_LABEL[o]}</option>)}</select>
                  <input className="sec-input" style={{ minWidth: 90, width: 110 }} type="number" min="0" step="any" required value={form.threshold} onChange={set('threshold')} />
                </div></div>
              <div className="sec-field"><label>Look back (minutes)</label>
                <input className="sec-input" type="number" min="5" max="10080" value={form.windowMinutes} onChange={set('windowMinutes')} /></div>
              <div className="sec-field"><label>Quiet period after firing (minutes)</label>
                <input className="sec-input" type="number" min="0" max="10080" value={form.cooldownMinutes} onChange={set('cooldownMinutes')} /></div>
              <div className="sec-field"><label>Severity</label>
                <select className="sec-select" value={form.severity} onChange={set('severity')}>
                  {meta.severities.map((s) => <option key={s}>{s}</option>)}</select></div>
              <label style={{ display: 'block', margin: '10px 0' }}>
                <input type="checkbox" checked={form.isEnabled} onChange={set('isEnabled')} /> Enabled</label>
              <p className="muted" style={{ fontSize: 12 }}>Rules are checked once a minute. Percentages (IVT rate) need at least 20 sessions in the window.</p>
              <div className="sec-row">
                <button className="primary" type="submit">Save rule</button>
                <button className="ghost" type="button" onClick={() => { setForm(null); setEditingId(null); }}>Cancel</button>
              </div>
            </form>
          )}

          <div className="sec-card sec-scroll">
            <table className="sec-table">
              <thead><tr><th>Name</th><th>Condition</th><th>Scope</th><th>Severity</th><th>Last fired</th><th>On</th><th /></tr></thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id}>
                    <td><b>{r.name}</b></td>
                    <td>{metricLabel(r.metric)} {OP_LABEL[r.operator]} {r.threshold} <span className="muted">/ {r.windowMinutes} min</span></td>
                    <td>{r.siteKey ? <span className="sec-mono">{r.siteKey.slice(0, 14)}…</span> : 'All my sites'}</td>
                    <td><SevBadge value={r.severity} /></td>
                    <td>{utc(r.lastTriggeredAt)}</td>
                    <td><RequirePermission code="alerts.manage" fallback={r.isEnabled ? 'Yes' : 'No'}>
                      <input type="checkbox" checked={r.isEnabled} onChange={(e) => act(() => setAlertRuleEnabled(r.id, e.target.checked))} /></RequirePermission></td>
                    <td><RequirePermission code="alerts.manage"><div className="sec-row">
                      <button className="ghost" onClick={() => edit(r)}>Edit</button>
                      <button className="ghost danger" onClick={() => window.confirm('Delete this rule?') && act(() => deleteAlertRule(r.id))}>Delete</button>
                    </div></RequirePermission></td>
                  </tr>
                ))}
                {rules.length === 0 && <tr><td colSpan="7" className="muted">No rules yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
